import { Dispatch, FC, MouseEvent, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { cloneDeep } from 'lodash-es';
import useResizeObserver from 'use-resize-observer';

import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useGesture } from '@use-gesture/react';

import { MenuItemChild } from '@da-g3-acl/shared/common';

import {
	deserialiseImageTypeId,
	serialiseImageTypeId,
	useMappedImageTypeMenuItems,
} from '../../../hooks/use-core-viewer-statusbar-menus';
import { ImageTypeLoaders } from '../../../types/imageTypeLoader';
import { ImageTypeMenuItem } from '../../../types/imageTypeMenuItem';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { PlusIcon } from '../../icons/PlusIcon';
import { XIcon } from '../../icons/XIcon';
import { StatusBarMenu } from '../StatusBarMenu';

import styles from './index.module.scss';

const trimMenuItems = (menuItems: MenuItemChild[], remove: number[]): MenuItemChild[] => {
	const parentsToCheck: string[] = [];
	const newItems = cloneDeep(menuItems).filter((x) => !remove.includes(deserialiseImageTypeId(x.id)));
	for (const item of newItems) {
		if (item.children && item.children.length > 0) {
			item.children = item.children?.filter((x) => !remove.includes(deserialiseImageTypeId(x.id)));
			parentsToCheck.push(item.id);
		}
	}
	return newItems.filter((x) => !parentsToCheck.includes(x.id) || !x.children || x.children.length > 0);
};

interface StatusBarImageTypesSelectorProps {
	loaders: ImageTypeLoaders;
	imageTypeMenuItems: ImageTypeMenuItem[] | undefined;
	selectedMenuItemIds?: number[];
	setSelectedMenuItemIds?: Dispatch<SetStateAction<number[]>>;
	isLoading?: boolean;
}

export const StatusBarImageTypesSelector: FC<StatusBarImageTypesSelectorProps> = (props) => {
	const { loaders, imageTypeMenuItems, selectedMenuItemIds, setSelectedMenuItemIds, isLoading = false } = props;

	const [showLeft, setShowLeft] = useState(false);
	const [showRight, setShowRight] = useState(false);

	const menuItems = useMappedImageTypeMenuItems(imageTypeMenuItems);

	const addPane = useCallback(
		(id: string | undefined) => {
			if (id) {
				const imageTypeId = deserialiseImageTypeId(id);
				setSelectedMenuItemIds?.((curr) => [...curr, imageTypeId]);
			}
		},
		[setSelectedMenuItemIds],
	);

	const changePaneType = useCallback(
		(id: string | undefined, index: number) => {
			if (id) {
				const imageTypeId = deserialiseImageTypeId(id);
				setSelectedMenuItemIds?.((curr) => {
					const clone = [...curr];
					clone.splice(index, 1, imageTypeId);
					return clone;
				});
			}
		},
		[setSelectedMenuItemIds],
	);

	const closePane = useCallback(
		(index: number, event: MouseEvent) => {
			setSelectedMenuItemIds?.((curr) => {
				const clone = [...curr];
				clone.splice(index, 1);
				return clone;
			});
			event.stopPropagation();
		},
		[setSelectedMenuItemIds],
	);

	const tabRef = useRef<HTMLDivElement>(null);

	const updateScrollButtons = useCallback(() => {
		if (tabRef.current) {
			setShowLeft(tabRef.current.scrollLeft > 0);
			setShowRight(tabRef.current.scrollLeft < tabRef.current.scrollWidth - tabRef.current.clientWidth);
		}
	}, []);

	useGesture(
		{
			onWheel: ({ delta: [, dy], event }) => {
				if (tabRef.current) {
					tabRef.current.scrollTo({ left: (tabRef.current.scrollLeft += dy * -0.2), behavior: 'smooth' });

					updateScrollButtons();
				}
				event.stopPropagation();
				event.preventDefault();
			},
		},
		{
			target: tabRef,
			eventOptions: { passive: false },
		},
	);

	const scroll = (left: boolean): void => {
		if (tabRef.current) {
			tabRef.current.scrollTo({ left: (tabRef.current.scrollLeft += left ? -50 : 50), behavior: 'smooth' });
			updateScrollButtons();
		}
	};

	const { width } = useResizeObserver({ ref: tabRef });
	useEffect(() => {
		if (tabRef.current) {
			setShowLeft(tabRef.current.scrollLeft > 0);
			setShowRight(tabRef.current.scrollLeft < tabRef.current.scrollWidth - tabRef.current.clientWidth);
		}
	}, [width]);

	const isOverflow = tabRef.current ? tabRef.current.scrollWidth > tabRef.current.clientWidth : false;

	const addImageTypeMenuItems = useMemo(
		() => trimMenuItems(menuItems, selectedMenuItemIds ?? []),
		[selectedMenuItemIds, menuItems],
	);

	const onDragEnd = (result: DropResult): void => {
		if (!selectedMenuItemIds || !result.destination) {
			return;
		}

		const clone = [...selectedMenuItemIds];
		const [removed] = clone.splice(result.source.index, 1);
		clone.splice(result.destination.index, 0, removed);

		setSelectedMenuItemIds?.(clone);
	};

	return (
		<div className={styles.imageTypeControls} data-statusbar-item-type="image-types-selector">
			{(imageTypeMenuItems?.length ?? 0) === 0 && <div className={styles.emptyTitle}>No Images</div>}
			{isOverflow && showLeft && (
				<button onClick={() => scroll(true)}>
					<ChevronLeftIcon />
				</button>
			)}
			<div className={styles.imageTypeTabs} ref={tabRef}>
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="droppable" direction="horizontal">
						{(provided) => (
							<div className={styles.dragList} ref={provided.innerRef} {...provided.droppableProps}>
								{selectedMenuItemIds?.map((imageTypeId, i) => {
									const imageType = imageTypeMenuItems?.find((x) => x.id === imageTypeId);
									const metadata = loaders[imageTypeId]?.imageTypeMetadata;
									if (!imageType) return;
									return (
										<Draggable
											key={imageTypeId}
											draggableId={`image-type-${imageTypeId}`}
											isDragDisabled={selectedMenuItemIds.length < 2}
											index={i}
										>
											{(provided) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
												>
													<StatusBarMenu
														key={`${imageTypeId}-${i}`}
														selectedMenuItemIds={[serialiseImageTypeId(imageTypeId)]}
														menuItems={menuItems ?? []}
														showTags={true}
														allowPin={true}
														tooltip={
															metadata?.modified
																? `Last updated: ${new Date(metadata.modified)}`
																: undefined
														}
														onItemSelected={(id) => changePaneType(id, i)}
														className={styles.imageSelector}
													>
														<span className={styles.name}>{imageType.name}</span>
														{selectedMenuItemIds.length > 1 && (
															<button title="Close Pane" onClick={(e) => closePane(i, e)}>
																<XIcon size={17} />
															</button>
														)}
													</StatusBarMenu>
												</div>
											)}
										</Draggable>
									);
								})}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</div>
			{isOverflow && showRight && (
				<button onClick={() => scroll(false)}>
					<ChevronRightIcon />
				</button>
			)}
			{addImageTypeMenuItems.length > 0 && (
				<StatusBarMenu
					selectedMenuItemIds={[]}
					menuItems={addImageTypeMenuItems ?? []}
					showTags={true}
					allowPin={false}
					onItemSelected={addPane}
					maxHeight={300}
					className={styles.addSelector}
					tooltip="Add Pane"
				>
					<PlusIcon size={16} />
				</StatusBarMenu>
			)}
			{isLoading && <span className={clsx(styles.loader, styles.isLoading)}></span>}
		</div>
	);
};
